/**
 * The scene: what one viewer believes is on each tile, built once per state change.
 *
 * The renderer used to answer these questions for itself, inline, every frame — is
 * this tile known, is there a city here and is it the live one or a memory, which
 * units can be seen. Those answers are not drawing; they are the fog-of-war rules, and
 * a second renderer (the planned dimetric view, ADR-0047) would have had to copy them.
 * Copied fog rules are how a resource leaks through the fog in one view and not the
 * other.
 *
 * So the scene owns the questions and the renderers own only the pictures. It reads
 * the game state and never changes it, and it is plain data plus lookups — nothing in
 * it knows about a canvas.
 */

import type { Content } from '../../content/schema/index.js';
import { cityIndex } from '../core/cities/index.js';
import { currentEra } from '../core/research/index.js';
import { SHOWN_BUILDINGS } from './models/city.js';
import { cargoOf, maxHp, unitDef, unitIndex } from '../core/units/index.js';
import { NONE, VIS_UNKNOWN, VIS_VISIBLE, type GameState, type Id, type TileGrid } from '../core/state/types.js';

export interface SceneCity {
  /** The live city's id, or null for a memory of a city the viewer cannot see now. */
  id: Id | null;
  owner: Id;
  name: string;
  population: number;
  colour: string;
  /** The owner's era (0 ancient … 5 modern), for the city's style (ADR-0076). */
  era: number;
  /** Content keys of the buildings that show on the map, wonders included; empty for a memory. */
  buildings: readonly string[];
  capital: boolean;
}

export interface SceneUnit {
  id: Id;
  owner: Id;
  type: number;
  /** The unit type's content key, which names its model (ADR-0075). */
  key: string;
  glyph: 'chevron' | 'shield' | 'disc' | 'ship' | 'siege' | 'plane';
  /** Rank index (0 Green) and how many units it carries, for the badges. */
  rank: number;
  cargo: number;
  /** Below decks: never the one drawn for its stack — the ship is. */
  aboard: boolean;
  colour: string;
  hp: number;
  maxHp: number;
}

/** Every unit the viewer can see on one tile, the one drawn on top first. */
export interface SceneStack {
  index: number;
  units: SceneUnit[];
}

export interface Scene {
  readonly map: TileGrid;
  readonly viewer: Id | null;
  /** VIS_* level for a tile. Without a viewer, everything is in plain sight. */
  seen(index: number): number;
  /** What the viewer believes is built on a tile: the truth where visible, memory elsewhere. */
  improvement(index: number): number;
  /** Whether the viewer believes a road runs here: the truth where visible, memory elsewhere. */
  road(index: number): boolean;
  /** The city the viewer believes stands here, live where visible and remembered elsewhere. */
  city(index: number): SceneCity | null;
  /** Units the viewer can see on a tile, or null. Only ever on VISIBLE tiles. */
  stackAt(index: number): SceneStack | null;
  /** Every visible stack, in tile order. */
  readonly stacks: readonly SceneStack[];
  /** Colour of the civilisation owning a known tile, or null. */
  ownerColour(index: number): string | null;
}

/**
 * Which unit in a stack is drawn on top: the one that would defend it — the strongest
 * defender, ties to the oldest — because that is what a glance at the map should say.
 */
function stackOrder(content: Content) {
  return (a: SceneUnit, b: SceneUnit): number =>
    Number(a.aboard) - Number(b.aboard) ||
    content.units.get(b.type).defence - content.units.get(a.type).defence ||
    a.id - b.id;
}

export function buildScene(state: GameState, content: Content, viewer: Id | null): Scene {
  const map = state.map;
  const vision = viewer === null ? null : (state.vision[viewer] ?? null);
  const memory = viewer === null ? null : (state.memory[viewer] ?? null);
  const seen = (index: number): number => (vision ? vision[index]! : VIS_VISIBLE);
  const colourOf = (civId: Id): string => state.civs[civId]?.colour ?? '#FFFFFF';

  const eraOf = (civId: Id): number => {
    const civ = state.civs[civId];
    return civ ? currentEra(content, civ) : 0;
  };
  const shown = (keys: string[]): string[] =>
    keys.filter((k) => SHOWN_BUILDINGS.includes(k) || content.buildings.get(content.buildings.id(k)).wonder);
  const live = new Map<number, SceneCity>();
  for (const city of Object.values(state.cities)) {
    const keys = city.buildings.map((b) => content.buildings.get(b).key);
    live.set(cityIndex(map, city), {
      id: city.id,
      owner: city.owner,
      name: city.name,
      population: city.population,
      colour: colourOf(city.owner),
      era: eraOf(city.owner),
      buildings: shown(keys),
      capital: keys.includes('palace'),
    });
  }

  const byTile = new Map<number, SceneStack>();
  for (const unit of Object.values(state.units).sort((a, b) => a.id - b.id)) {
    const index = unitIndex(map, unit);
    if (index === -1 || seen(index) !== VIS_VISIBLE) continue;
    let stack = byTile.get(index);
    if (!stack) {
      stack = { index, units: [] };
      byTile.set(index, stack);
    }
    stack.units.push({
      id: unit.id,
      owner: unit.owner,
      type: unit.type,
      key: content.units.get(unit.type).key,
      glyph: content.units.get(unit.type).glyph,
      colour: colourOf(unit.owner),
      hp: unit.hp,
      maxHp: maxHp(content, unit),
      rank: unit.experience,
      aboard: unit.aboard !== NONE,
      cargo: unitDef(content, unit).capacity > 0 || unitDef(content, unit).airCapacity > 0 ? cargoOf(state, unit).length : 0,
    });
  }
  const order = stackOrder(content);
  for (const stack of byTile.values()) stack.units.sort(order);
  const stacks = [...byTile.values()].sort((a, b) => a.index - b.index);

  return {
    map,
    viewer,
    seen,
    stacks,
    improvement(index) {
      const level = seen(index);
      if (level === VIS_UNKNOWN) return 0;
      if (!vision || level === VIS_VISIBLE) return map.improvement[index]!;
      return memory?.improvement[index] ?? 0;
    },
    road(index) {
      const level = seen(index);
      if (level === VIS_UNKNOWN) return false;
      if (!vision || level === VIS_VISIBLE) return map.road[index] === 1;
      return memory?.road[index] === 1;
    },
    city(index) {
      const level = seen(index);
      if (level === VIS_UNKNOWN) return null;
      if (!vision || level === VIS_VISIBLE) return live.get(index) ?? null;
      const recalled = memory?.cities[index];
      if (!recalled) return null;
      return {
        id: null,
        owner: recalled.owner,
        name: recalled.name,
        population: recalled.population,
        colour: colourOf(recalled.owner),
        era: eraOf(recalled.owner),
        buildings: [],
        capital: false,
      };
    },
    stackAt(index) {
      return byTile.get(index) ?? null;
    },
    ownerColour(index) {
      if (seen(index) === VIS_UNKNOWN) return null;
      const owner = map.owner[index]!;
      if (owner === NONE) return null;
      return state.civs[owner]?.colour ?? null;
    },
  };
}
